import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiLink } from '@remixicon/react';
import {
  createFrontendModule,
  useAnalytics,
} from '@backstage/frontend-plugin-api';
import {
  TechDocsAddonLocations,
  useShadowRootElements,
} from '@backstage/plugin-techdocs-react';
import { AddonBlueprint } from '@backstage/plugin-techdocs-react/alpha';
import { Tooltip, TooltipTrigger } from '@backstage/ui';
import { useLocation } from 'react-router-dom';

const STATUS_RESET_MS = 1500;
const LABEL = 'Copy link to this section';
const MAX_REASON_LENGTH = 40;

// Browsers phrase the reason differently, for example Chrome's
// "Failed to execute 'writeText' on 'Clipboard': Document is not focused."
const failureMessage = (error: unknown) => {
  const reason =
    error instanceof Error
      ? error.message.split(': ').pop()?.replace(/\.$/, '')
      : undefined;
  return reason && reason.length <= MAX_REASON_LENGTH
    ? `Couldn't copy the link: ${reason}`
    : "Couldn't copy the link";
};

/**
 * Copies a section's URL when its heading anchor is clicked, shows it in the
 * address bar without scrolling, and confirms the copy next to the anchor.
 *
 * The TechDocs reader attaches its own click listener to every link first,
 * which scrolls the heading to the top. This listener uses the capture phase
 * so it runs before the reader's, then stops it, and records the same click
 * event the reader would have. Cmd/Ctrl-click is left to the reader, which
 * opens the section in a new tab.
 *
 * When a page opens at a section, the reader also focuses that section's
 * anchor, which highlights it. The add-on removes that one focus; later
 * keyboard focus is kept.
 */
export const CopyHeadingLinkAddon = () => {
  const analytics = useAnalytics();
  const found = useShadowRootElements<HTMLAnchorElement>(['a.headerlink']);
  // The hook returns a new array on every render. Keeping the previous one
  // while it holds the same anchors stops the effects below from running
  // again after every copy.
  const stableAnchors = useRef(found);
  if (
    found.length !== stableAnchors.current.length ||
    found.some((anchor, index) => anchor !== stableAnchors.current[index])
  ) {
    stableAnchors.current = found;
  }
  const anchors = stableAnchors.current;
  const [message, setMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const clickedAnchor = useRef<HTMLAnchorElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // The reader focuses the anchor of the section in the router's location,
  // which the add-on's own address bar updates don't change.
  const { hash } = useLocation();
  const unfocusedAnchor = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    const target = anchors.find(anchor => hash && anchor.hash === hash);
    if (!target || target === unfocusedAnchor.current) return undefined;
    const removeFocus = () => {
      unfocusedAnchor.current = target;
      target.blur();
    };
    const root = target.getRootNode() as Document | ShadowRoot;
    if (root.activeElement === target) {
      removeFocus();
      return undefined;
    }
    target.addEventListener('focus', removeFocus, { once: true });
    return () => target.removeEventListener('focus', removeFocus);
  }, [anchors, hash]);

  useEffect(() => {
    const symbols = anchors.map(anchor => {
      const text = Array.from(anchor.childNodes).filter(
        node => node.nodeType === Node.TEXT_NODE,
      );
      text.forEach(node => node.remove());
      return text;
    });

    const copyLink = (event: MouseEvent) => {
      if (event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const anchor = event.currentTarget as HTMLAnchorElement;
      // Replacing keeps the router's state and adds no Back step for a page
      // that didn't move.
      window.history.replaceState(window.history.state, '', anchor.hash);
      const url = anchor.href;
      // The reader labels the event with the anchor's text, which was removed.
      const subject = symbols[anchors.indexOf(anchor)]
        .map(node => node.textContent)
        .join('');
      analytics.captureEvent('click', subject || url, {
        attributes: { to: url.replace(window.location.origin, '') },
      });
      const report = (next: string) => {
        clickedAnchor.current = anchor;
        setMessage(next);
        setIsOpen(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setIsOpen(false), STATUS_RESET_MS);
      };
      navigator.clipboard.writeText(url).then(
        () => report('Link copied'),
        error => report(failureMessage(error)),
      );
    };

    anchors.forEach(anchor => {
      anchor.title = LABEL;
      anchor.setAttribute('aria-label', LABEL);
      anchor.addEventListener('click', copyLink, { capture: true });
    });
    return () => {
      anchors.forEach((anchor, index) => {
        anchor.removeEventListener('click', copyLink, { capture: true });
        anchor.removeAttribute('aria-label');
        anchor.prepend(...symbols[index]);
      });
    };
  }, [anchors, analytics]);

  // The trigger only supplies the open state: the tooltip is positioned at the
  // clicked anchor, which lives in the docs page's shadow DOM.
  return (
    <>
      {anchors.map((anchor, index) =>
        createPortal(
          // App stylesheets don't reach the docs page's shadow DOM.
          <RiLink
            aria-hidden
            size="1em"
            style={{ verticalAlign: '-0.125em' }}
          />,
          anchor,
          index,
        ),
      )}
      <TooltipTrigger isOpen={isOpen}>
        <Tooltip triggerRef={clickedAnchor} placement="right">
          {message}
        </Tooltip>
      </TooltipTrigger>
    </>
  );
};

const techDocsCopyHeadingLinkAddon = AddonBlueprint.make({
  name: 'copy-heading-link',
  params: {
    name: 'CopyHeadingLink',
    location: TechDocsAddonLocations.Content,
    component: CopyHeadingLinkAddon,
  },
});

export const techDocsCopyHeadingLinkAddonModule = createFrontendModule({
  pluginId: 'techdocs',
  extensions: [techDocsCopyHeadingLinkAddon],
});
